import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';

import { TABLE_HEIGHT_CONSTANTS } from '../../const/CommonConst';

/**
 * Custom hook to calculate maximum table height for virtualization
 * @param {Object} options - Configuration options
 * @param {number} [options.headerHeight] - Header height (default: 56)
 * @param {number} [options.titleHeight] - Title section height (default: 50)
 * @param {number} [options.tabsHeight] - Tabs height (default: 48)
 * @param {number} [options.searchBarHeight] - Search bar height (default: 80)
 * @param {number} [options.paginationHeight] - Pagination controls height (default: 60)
 * @param {number} [options.paddingAndMargins] - Additional padding (default: 40)
 * @param {number} [options.adjustedHeight] - Any additional height adjustments (default: 0)
 * @returns {{windowHeight: number, maxTableHeight: number}} Window height and maximum table height
 */
export const useTableMaxHeight = (options = {}) => {
  const {
    headerHeight = TABLE_HEIGHT_CONSTANTS.HEADER_HEIGHT,
    titleHeight = TABLE_HEIGHT_CONSTANTS.TITLE_HEIGHT,
    tabsHeight = TABLE_HEIGHT_CONSTANTS.TABS_HEIGHT,
    searchBarHeight = TABLE_HEIGHT_CONSTANTS.SEARCH_BAR_HEIGHT,
    paginationHeight = TABLE_HEIGHT_CONSTANTS.PAGINATION_HEIGHT,
    paddingAndMargins = TABLE_HEIGHT_CONSTANTS.PADDING_AND_MARGINS,
    adjustedHeight = 0,
  } = options;

  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  const handleResize = useCallback(() => {
    setWindowHeight(window.innerHeight);
  }, [setWindowHeight]);

  useEffect(() => {
    const debouncedHandleResize = debounce(handleResize, 150);

    window.addEventListener('resize', debouncedHandleResize);
    return () => {
      debouncedHandleResize.cancel();
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [handleResize]);

  const maxTableHeight = useMemo(() => {
    const availableHeight =
      windowHeight -
      headerHeight -
      titleHeight -
      tabsHeight -
      searchBarHeight -
      paginationHeight -
      paddingAndMargins +
      adjustedHeight;

    return availableHeight;
  }, [
    windowHeight,
    headerHeight,
    titleHeight,
    tabsHeight,
    searchBarHeight,
    paginationHeight,
    paddingAndMargins,
  ]);

  return { windowHeight, maxTableHeight };
};
